import Pusher from 'pusher-js'
import axios from 'axios'
import swal from 'sweetalert2/dist/sweetalert2'
import colors from 'tailwindcss/colors'
import $ from 'jquery'
import { find, forEach, extend } from 'lodash'
import { parseFullName } from 'parse-full-name'
import { trans } from '@/munio/i18n'
import filesize from '@/libs/filesize.js'
import './counters.js'

/**
 * Set page title
 *
 * @param {string} title
 */
Munio.setTitle = function (title) {
  document.title = `${title} - ${Munio.config.sitename}`
  document.querySelector('#header .header-title__title').textContent = title
}

Munio.setSubtitle = function (subtitle) {
  const detailsEl = document.querySelector('#header .header-details')
  const subtitleEl = detailsEl.querySelector('.header-subtitle')

  if (subtitle) {
    if (!subtitleEl) {
      detailsEl.insertAdjacentHTML('afterbegin', `<div class="header-subtitle">${subtitle}</div>`)
    } else {
      subtitleEl.innerHTML = subtitle
    }
  } else {
    subtitleEl?.remove()
  }
}

//
// Colors
//

Munio.chartColors = [
  colors.blue[600],
  colors.green[600],
  colors.yellow[500],
  colors.red[600],
  colors.fuchsia[600],
  colors.cyan[600],
  colors.orange[500],
  colors.lime[600],
  colors.sky[600],
  colors.rose[600],
  colors.teal[600],
  colors.indigo[500],
  colors.emerald[600],
  colors.stone[500],
  colors.pink[600],
  colors.slate[500],
]

Munio.ecoProduct = (product) => {
  return Munio.config.ecoonline.products.find((ecoProduct) => ecoProduct.key === product)
}

/**
 * Name parser
 *
 * @param {string} name
 * @returns {{first: string, last: string}}
 */
Munio.parseName = (name) => {
  const { first, middle, last } = parseFullName(name.trim())

  return {
    first: [first, middle].join(' ').trim(),
    last: last,
  }
}

/**
 * Attach behaviors
 */
Munio.attachBehaviors = (context, config) => {
  context = context || document.getElementById('page')
  config = config || Munio.config

  // Execute all of them.
  forEach(Munio.behaviors, (behavior) => {
    // Create a new instance of the behavior to
    // avoid overidden functions or variables.
    const instance = extend({}, behavior)

    if (typeof instance.attach !== 'function') {
      return
    }

    if (instance.selector) {
      // Skip behavior if the element is not found
      if ($(instance.selector, context).length === 0) {
        return
      }
    }

    // Bind the "attach" function to
    // the behavior and run it.
    instance.attach.bind(instance)
    instance.attach(context, config)
  })
}

/**
 * Global handler for the data object from JSON responses.
 *
 * @param {Object} data
 * @return  bool
 */
Munio.handleJSONResponseData = function (data) {
  if (!data) {
    return true
  }

  if (Array.isArray(data.toasts)) {
    data.toasts.forEach((toast) => Munio.Notifications.show(toast))
  }

  if (data.redirect) {
    if (window.location.href === data.redirect) {
      window.location.reload()
    } else {
      window.location = data.redirect
    }

    return false
  }

  if (data.refresh) {
    window.location.reload()
  }

  return true
}

Munio.config.csv = {
  header: true,
  delimiter: 'comma',
}

Munio.downloadCSV = async function (baseUrl, options = {}, urlParams = window.location.search) {
  const settings = Munio.Storage.getWithDefault('csvSettings', Munio.config.csv)

  if (typeof options.header === 'boolean') {
    settings.header = options.header
  }
  if (typeof options.delimiter === 'string') {
    settings.delimiter = options.delimiter
  }
  Munio.Storage.set('csvSettings', settings)

  const parameters = Munio.urlParams(urlParams)
  parameters.set('export', 'csv')
  parameters.set('csv[header]', settings.header)
  parameters.set('csv[delimiter]', settings.delimiter)
  if (options.id) {
    parameters.set('csv[id]', options.id)
  }

  $(window).bind('beforeunload', window.blockNavigate)

  try {
    const { data, headers } = await Munio.$http.get(baseUrl, parameters, {}, { responseType: 'blob' })
    const _mime = headers['content-type'] || 'text/csv;charset=utf-8'
    const _filename = window.extractDispositionFilename(headers['content-disposition'])
    window.downloadFile(data, _filename, _mime)
  } catch (err) {
    console.error(err)
    await Munio.alert(err.toString())
  } finally {
    $(window).unbind('beforeunload', window.blockNavigate)
  }
}

/**
 * Get the flag emoji for a country.
 */
Munio.flag = function (country) {
  return country.toUpperCase().replaceAll(/./g, (char) => String.fromCodePoint(char.codePointAt(0) + 127397))
}

/**
 * Get the country model
 */
Munio.country = function (iso) {
  return Munio.config.countries.find((country) => country.iso === iso.toLowerCase())
}

/**
 * Get all the visible country models (sorted)
 */
Munio.countries = function (local = true) {
  const sortKey = local ? 'i18n' : 'name'

  return Munio.config.countries
    .filter((country) => country.isVisible)
    .sort((a, b) => a[sortKey].localeCompare(b[sortKey], Munio.config.i18n.language))
}

/**
 * Get all the languages (sorted)
 *
 * @param {boolean} local
 * @param {boolean} translated
 * @returns {Array<{
 *  key: string,
 *  language: string,
 *  country: string,
 *  locale: string,
 *  title: string,
 *  name: string,
 *  flag: string,
 *  translated: boolean,
 * }>}
 */
Munio.languages = function (local = true, translated = true) {
  const sortKey = local ? 'name' : 'title'

  return Munio.config.languages
    .filter((l) => (translated ? l.translated : true))
    .sort((a, b) => a[sortKey].localeCompare(b[sortKey], Munio.config.i18n.language))
}

/**
 * Websocket
 */
let pusher

Munio.Websocket = function () {
  if (pusher) return pusher

  const { key, options } = Munio.config.websocket

  pusher = new Pusher(key, options)

  pusher.private = function (channel) {
    return pusher.subscribe('private-' + channel)
  }

  pusher.presence = function (channel) {
    return pusher.subscribe('presence-' + channel)
  }

  axios.interceptors.request.use((config) => {
    if (pusher.connection.socket_id) {
      config.headers['X-Socket-Id'] = pusher.connection.socket_id
    }

    return config
  })

  if (typeof $.ajax !== 'undefined') {
    $.ajaxPrefilter((options, originalOptions, xhr) => {
      if (pusher.connection.socket_id) {
        xhr.setRequestHeader('X-Socket-Id', pusher.connection.socket_id)
      }
    })
  }

  if (isDevMode) {
    pusher.connection.bind_global(function () {
      console.debug('[PUSHER]', ...arguments)
    })
  }

  return pusher
}

/**
 * Language getter
 *
 * @param language
 * @return object
 */
Munio.language = function (language) {
  return (
    find(this.config.languages, { key: language }) ||
    find(this.config.languages, { locale: language }) ||
    find(this.config.languages, { language })
  )
}

/**
 * Show a flash message
 *
 * @type {{message, success, info}}
 * @link https://limonte.github.io/sweetalert2/
 */
Munio.Flash = (function () {
  const parseArguments = function (title, text, options = {}) {
    if (typeof title !== 'string' && title !== null) {
      options = title
      title = text = null
    }

    if (typeof text !== 'string' && text !== null) {
      options = text
      text = null
    }

    if (!title) {
      title = trans('Are you sure?')
    }

    return { title, text, options }
  }

  const message = function (title, text, options = {}, defaultOptions = {}) {
    ;({ title, text, options } = parseArguments(title, text, options))

    const globalOptions = {
      reverseButtons: true,
      buttonsStyling: false,
      allowEnterKey: false,
      cancelButtonClass: 'mdl-button mdl-js-button',
      cancelButtonText: trans('Cancel'),
      confirmButtonClass: 'mdl-button mdl-js-button mdl-button--raised mdl-button--primary',
      confirmButtonText: trans('OK'),
    }

    return swal(Object.assign({}, globalOptions, defaultOptions, options, { html: text || undefined, title }))
  }

  return {
    message,
    success: (title = trans('Success'), text = undefined, options = {}) => {
      return message(title, text, options, { type: 'success', showConfirmButton: false, timer: 1800 })
    },
    info: (title, text, options = {}) => {
      console.info(title, text)
      return message(title, text, options, { type: 'info' })
    },
    error: (title, text, options = {}) => {
      console.error(title, text)
      return message(title, text, options, { type: 'error' })
    },
    warning: (title, text, options = {}) => {
      console.warn(title, text)
      return message(title, text, options, { type: 'warning' })
    },
    question: (title, text, options = {}) => {
      return message(title, text, options, { type: 'question' })
    },
  }
})()

/**
 * Show a confirmation dialog
 *
 * @param {string?} [title]
 * @param {string?} [text]
 * @param {object?} [options]
 * @returns {Promise<number|string|boolean>}
 */
Munio.confirm = async function (title = undefined, text = undefined, options = {}) {
  const response = await Munio.Flash.message(title, text, options, {
    type: 'question',
    showCancelButton: true,
    confirmButtonText: trans('Confirm'),
  })

  if (response.dismiss) {
    return false
  }

  return response.value ?? false
}

/**
 * Show an alert dialog
 *
 * @param title
 * @param text
 * @param options
 * @returns {Promise<boolean>}
 */
Munio.alert = async function (title, text, options = {}) {
  return (await Munio.Flash.info(title, text, options)).value || false
}

/**
 * Show a prompt dialog
 *
 * @param title
 * @param text
 * @param options
 * @returns {Promise<any>}
 */
Munio.prompt = async function (title, text, options = {}) {
  const defaultOptions = {
    type: 'question',
    input: 'text',
    showCancelButton: true,
  }

  return (await Munio.Flash.message(title, text, Object.assign({}, defaultOptions, options))).value
}

/**
 * @param {string} id
 * @param {object} data
 * @param {Function} onChange
 * @returns {Promise<Api12>}
 */
Munio.Scorm12 = async function (id, data, onChange) {
  const Api = (await import('./scorm/Api12/index.js')).default
  const scorm = new Api(id, data)
  scorm.onChange(onChange)
  return scorm
}

/**
 * @param {string} id
 * @param {object} data
 * @param {Function} onChange
 * @returns {Promise<Api2004>}
 */
Munio.Scorm2004 = async function (id, data, onChange) {
  const Api = new (await import('./scorm/Api2004/index.js')).default()
  const scorm = new Api(id, data)
  scorm.onChange(onChange)
  return scorm
}

Munio.filesize = filesize

export default Munio
